import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableSelect - A searchable dropdown component
 * @param {Object} props
 * @param {Array} props.options - Array of {value: string, label: string}
 * @param {string} props.value - Selected value
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.emptyText - Text to show when no results found
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.className - Additional classes for the button
 * @param {string} props.contentClassName - Additional classes for the popover content
 * @param {boolean} props.allowClear - Allow clearing by selecting the current value
 * @param {string} props.ariaLabel - Accessible name of the control
 * @param {string} props.ariaLabelledBy - Id of the element naming the control
 * @param {boolean} props.lockSingleOption - Auto-select and lock the control when
 *   there's exactly one selectable option (default true). Set to false for filters,
 *   where narrowing to one match shouldn't silently pin the selection — a later
 *   option appearing (e.g. a newly created item) would otherwise stay filtered out.
 */
export const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder,
  emptyText,
  searchPlaceholder,
  disabled = false,
  className,
  contentClassName,
  allowClear = false,
  disablePortal = false,
  lockSingleOption = true,
  ariaLabel,
  ariaLabelledBy,
}) => {
  const { t } = useTran();
  const [open, setOpen] = React.useState(false);
  const resolvedPlaceholder = placeholder ?? t("common.select_option");
  const resolvedEmptyText = emptyText ?? t("common.no_option_found");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("common.search...");
  const selectableOptions = React.useMemo(
    () => options.filter((option) => option.value !== "All"),
    [options],
  );
  const singleSelectableOption =
    lockSingleOption && selectableOptions.length === 1
      ? selectableOptions[0]
      : null;
  const resolvedOptions = singleSelectableOption ? selectableOptions : options;
  const isSingleOptionLocked = Boolean(singleSelectableOption);

  const selectedOption = resolvedOptions.find(
    (option) => option.value === value,
  );

  React.useEffect(() => {
    if (!singleSelectableOption) return;

    const singleValue = singleSelectableOption.value;

    if (value !== singleValue) {
      onChange?.(singleValue);
    }
  }, [onChange, singleSelectableOption, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className={cn(
            "h-9 w-full justify-between bg-transparent px-3 py-1 text-base font-normal dark:bg-background/30 md:text-sm",
            className,
          )}
          disabled={disabled}
        >
          <span className="text-left whitespace-normal break-words">
            {selectedOption ? selectedOption.label : resolvedPlaceholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        disablePortal={disablePortal}
        onWheel={(e) => e.stopPropagation()}
        className={cn(
          "w-auto min-w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1rem)] overflow-hidden p-0",
          contentClassName,
        )}
      >
        <Command>
          <CommandInput placeholder={resolvedSearchPlaceholder} />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>{resolvedEmptyText}</CommandEmpty>
            <CommandGroup>
              {resolvedOptions.map((option) => {
                const optionValue = option.value ?? "";
                const optionLabel = String(
                  option.label ?? option.value ?? option.key ?? "",
                );
                const optionMeta = option.meta
                  ? String(option.meta)
                  : option.code
                    ? String(option.code)
                    : "";
                return (
                  <CommandItem
                    key={optionValue}
                    value={`${optionLabel} ${optionMeta}`.trim()}
                    disabled={option.disabled}
                    className={cn(
                      option.disabled && "cursor-not-allowed opacity-60",
                    )}
                    onSelect={() => {
                      if (option.disabled || isSingleOptionLocked) return;
                      const nextValue =
                        allowClear && optionValue === value ? "" : optionValue;
                      onChange(nextValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === optionValue ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span
                      data-slot="searchable-select-label"
                      className="flex-1 whitespace-normal break-words"
                    >
                      {optionLabel}
                    </span>
                    {/* {optionMeta ? (
                      <span className="ml-3 shrink-0 text-xs font-medium text-muted-foreground">
                        {optionMeta}
                      </span>
                    ) : null} */}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
